import type { DynamicEventArgs } from "@asurion-core/eventualize";
import type { Logger } from "@expert/logging";

import { sendMetrics } from "@expert/cloudwatch-metrics";
import { trackFullStoryEvent } from "@expert/monitoring";

import type { RootDispatcherType } from "@/analytics";
import type { ApplicationEvents } from "@/analytics/eventTypes";

import { type ChatRuntimeAction, emitChatRuntimeError } from "./emitChatRuntimeError";

/** Events with a payload, since FullStory needs an object to project. */
type EventWithPayload = {
    [Name in keyof ApplicationEvents]: ApplicationEvents[Name] extends object ? Name : never;
}[keyof ApplicationEvents];

export type ChatEventMetric = Parameters<typeof sendMetrics>[0][number];

export interface ChatEventLog {
    level: "info" | "warn" | "error";
    message: string;
    fields: Record<string, unknown>;
}

export interface ChatEventRuntimeError {
    action: ChatRuntimeAction;
    partner?: string;
}

export interface ChatEventOptions {
    /** Also project the payload onto a FullStory event. */
    fullStory?: boolean;
    log?: ChatEventLog;
    metric?: ChatEventMetric;
    /** Also count the event on the `ChatRuntimeError` error metric. */
    runtimeError?: ChatEventRuntimeError;
}

/**
 * Reports one chat event to analytics (always) and, opt-in, FullStory, a log, a metric and the
 * runtime-error counter. The log and metric take their own fields rather than the payload, which
 * carries per-chat values that would explode Mimir label cardinality. Fire-and-forget.
 */
export function reportChatEvent<TEvent extends EventWithPayload>(
    { dispatcher, logger }: { dispatcher: RootDispatcherType; logger: Logger },
    eventName: TEvent,
    payload: ApplicationEvents[TEvent],
    { fullStory = false, log, metric, runtimeError }: ChatEventOptions = {},
): void {
    // eventualize's conditional rest tuple can't be resolved for a generic event name.
    void dispatcher.dispatchBusinessEvent(eventName, ...([payload] as DynamicEventArgs<ApplicationEvents, TEvent>));
    if (fullStory) trackFullStoryEvent(eventName, payload);
    if (log) logger[log.level](log.fields, log.message);
    if (metric) void sendMetrics([metric]);
    if (runtimeError) emitChatRuntimeError(runtimeError.action, runtimeError.partner);
}
